'use client';

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { PurchaseTicketDialog } from './PurchaseTicketDialog';
import { EVENT_STATUS, EventStatus } from '@/lib/schemas/event';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface Ticket {
	id: string;
	name: string;
	price: number;
	quantity: number;
	remaining_quantity: number;
}

interface TicketSectionProps {
	tickets: Ticket[];
	eventId: string;
	status: EventStatus;
}

export function TicketSection({
	tickets,
	eventId,
	status,
}: TicketSectionProps) {
	const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);

	//console.log('TicketSection props:', { tickets, eventId });

	const handleSelect = (ticket: Ticket) => {
		console.log('Selected ticket:', ticket);
		setSelectedTicket(ticket);
		setDialogOpen(true);
	};

	if (status === EVENT_STATUS.PENDING) {
		return (
			<div className='bg-card rounded-lg p-6 shadow-lg'>
				<Alert className='bg-yellow-500/10 text-yellow-500 border-yellow-500/50'>
					<AlertCircle className='h-4 w-4' />
					<AlertDescription>
						This event is pending approval. Tickets will be available once
						approved.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	if (status === EVENT_STATUS.REJECTED) {
		return (
			<div className='bg-card rounded-lg p-6 shadow-lg sticky top-4'>
				<Alert variant='destructive'>
					<AlertCircle className='h-4 w-4' />
					<AlertDescription>
						This event has been rejected and tickets are not available for
						purchase.
					</AlertDescription>
				</Alert>
			</div>
		);
	}

	return (
		<>
			<div className='bg-card rounded-lg p-6 shadow-lg sticky top-4'>
				<h2 className='text-xl font-semibold mb-4'>Tickets</h2>
				<div className='space-y-4'>
					{tickets.map((ticket) => (
						<div
							key={ticket.id}
							className='p-4 border rounded-lg hover:border-primary transition-colors'
						>
							<div className='flex justify-between items-start mb-2'>
								<div>
									<h3 className='font-medium'>{ticket.name}</h3>
									<p className='text-sm text-muted-foreground'>
										{ticket.remaining_quantity.toLocaleString()} remaining
									</p>
								</div>
								<span className='font-semibold'>
									{ticket.price === 0 ? 'Free' : `GH₵${ticket.price}`}
								</span>
							</div>
							<Button
								className='w-full'
								onClick={() => handleSelect(ticket)}
								disabled={ticket.remaining_quantity === 0}
							>
								{ticket.remaining_quantity === 0
									? 'Sold Out'
									: ticket.price === 0
										? 'RSVP'
										: 'Select'}
							</Button>
						</div>
					))}
				</div>
			</div>

			{selectedTicket && (
				<PurchaseTicketDialog
					open={dialogOpen}
					onOpenChange={setDialogOpen}
					ticket={selectedTicket}
					eventId={eventId}
				/>
			)}
		</>
	);
}
